/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * RouteDrawer
 * -----------
 * Draw a course on the map, one click per point.
 *
 *  • Click the map         → adds a point at the end of the course.
 *  • Click the line        → inserts a point there, to bend an existing leg.
 *  • Drag a point          → moves it.
 *  • Right-click a point   → removes it (long-press on a phone).
 *
 * Controlled: `points` in, `onChange(nextPoints)` out, so the standalone Route
 * builder and the event form's course editor share every behaviour. Undo is
 * kept here because it is about this editing session, not about the form.
 *
 * Straight lines between points, no road snapping — that needs a routing
 * service and a key (phase 2). Points placed at each corner give a line a watch
 * can follow.
 *
 * Tiles are the same keyless tile.openstreetmap.org the rest of the organizer
 * uses — see the tile-policy TODO in MapPointPicker.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, Polyline, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { isLoop, totalDistanceKm } from '../../athlohive/gpx/routeMath';
import { DAWN } from '../../../theme/organizerTokens';
import PlaceSearch from './PlaceSearch';

// Leaflet writes a path's colour as an SVG attribute, where CSS var() does not
// resolve — so the Dawn accent goes in as its literal value.
const LINE_COLOUR = DAWN.accent;

/** Bengaluru — only seen before the first point. */
const FALLBACK_CENTER = [12.9716, 77.5946];
const FALLBACK_ZOOM = 13;
/** Above this, individual points are not draggable: the handles would bury the line. */
export const MAX_HANDLES = 250;
const UNDO_LIMIT = 100;

/**
 * A placed point, rounded once to 6 decimals (~11 cm) at the moment it is
 * placed. From then on the same numbers go into the stored map line and into
 * the GPX file, so the two carry identical coordinates.
 */
const round6 = (value) => Math.round(Number(value) * 1e6) / 1e6;
export const placed = (p) => ({ lat: round6(p.lat), lng: round6(p.lng) });

const VERTEX_ICON = L.divIcon({
  className: 'orgr-vertex',
  html: '<span class="orgr-vertex__dot" aria-hidden="true"></span>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});
const START_ICON = L.divIcon({
  className: 'orgr-endpin',
  html: '<span class="orgr-endpin__dot orgr-endpin__dot--start" aria-hidden="true">S</span>',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});
const FINISH_ICON = L.divIcon({
  className: 'orgr-endpin',
  html: '<span class="orgr-endpin__dot orgr-endpin__dot--finish" aria-hidden="true">F</span>',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

function AddOnClick({ onAdd, enabled }) {
  useMapEvents({
    click: (event) => { if (enabled) onAdd({ lat: event.latlng.lat, lng: event.latlng.lng }); },
  });
  return null;
}

/**
 * Frames the whole course whenever `fitKey` changes — on open, and after a GPX
 * is loaded. Not on every edit: re-framing under the cursor while somebody is
 * placing points would move the map out from under them.
 */
function FitToCourse({ points, fitKey }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !points || points.length === 0) return;
    if (points.length === 1) {
      if (typeof map.setView === 'function') map.setView([points[0].lat, points[0].lng], 15);
      return;
    }
    if (typeof map.fitBounds === 'function') {
      map.fitBounds(points.map((p) => [p.lat, p.lng]), { padding: [30, 30] });
    }
    // Only when the key changes, deliberately — see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, fitKey]);
  return null;
}

/** Street level: close enough to put a start line on the right side of a gate. */
const SEARCH_ZOOM = 17;

/** Moves the map to a searched place. Keyed, so picking the same place twice still moves it. */
function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !target) return;
    if (typeof map.flyTo === 'function') map.flyTo([target.lat, target.lng], SEARCH_ZOOM, { duration: 0.8 });
    else if (typeof map.setView === 'function') map.setView([target.lat, target.lng], SEARCH_ZOOM);
    // Keyed on the pick, not the object: marking a pick as used must not fly
    // the map back to it after the organizer has panned away.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, target?.key]);
  return null;
}

/** Index of the leg (i → i+1) nearest to `p`, on a flat local projection. */
function nearestLeg(points, p) {
  const kx = Math.cos((p.lat * Math.PI) / 180);
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < points.length - 1; i += 1) {
    const a = points[i]; const b = points[i + 1];
    const bx = (b.lng - a.lng) * kx; const by = b.lat - a.lat;
    const px = (p.lng - a.lng) * kx; const py = p.lat - a.lat;
    const len2 = bx * bx + by * by;
    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, (px * bx + py * by) / len2));
    const d = Math.hypot(px - t * bx, py - t * by);
    if (d < bestDist) { bestDist = d; best = i; }
  }
  return best;
}

export default function RouteDrawer({
  points = [],
  onChange,
  fitKey = 0,
  height = 460,
  readOnly = false,
  scrollWheelZoom = true,
}) {
  const [history, setHistory] = useState([]);
  // The place last picked in the search box, and whether it can still be added.
  const [searchTarget, setSearchTarget] = useState(null);
  // Latest points without re-binding every Leaflet handler on each edit.
  const pointsRef = useRef(points);
  pointsRef.current = points;

  // A new course from outside (opened, GPX loaded, cleared by the parent)
  // starts a fresh editing session: undoing into a different course is not undo.
  useEffect(() => { setHistory([]); }, [fitKey]);

  const commit = (next) => {
    setHistory((previous) => [...previous.slice(-(UNDO_LIMIT - 1)), pointsRef.current]);
    onChange(next);
  };

  const addPoint = (p) => commit([...pointsRef.current, placed(p)]);

  const insertPoint = (p) => {
    const current = pointsRef.current;
    if (current.length < 2) { addPoint(p); return; }
    const leg = nearestLeg(current, p);
    commit([...current.slice(0, leg + 1), placed(p), ...current.slice(leg + 1)]);
  };

  const movePoint = (index, p) => commit(pointsRef.current.map((q, i) => (i === index ? placed(p) : q)));

  const removePoint = (index) => commit(pointsRef.current.filter((_, i) => i !== index));

  const undo = () => {
    if (!history.length) return;
    const previous = history[history.length - 1];
    setHistory(history.slice(0, -1));
    onChange(previous);
  };

  const closeLoop = () => {
    const current = pointsRef.current;
    if (current.length < 3 || isLoop(current, 0.001)) return;
    commit([...current, { ...current[0] }]);
  };

  const reverse = () => commit([...pointsRef.current].reverse());

  const clear = () => { if (pointsRef.current.length) commit([]); };

  /**
   * A searched place. On an empty course it becomes the start — that is what
   * the search is for. On a course already drawn it only moves the map and
   * offers to add the place as the next point: a search must never quietly
   * rewrite a course somebody has spent time on.
   */
  const onSearchPick = (place) => {
    const target = { ...place, key: Date.now() };
    if (!pointsRef.current.length) {
      commit([placed(place)]);
      setSearchTarget({ ...target, used: true });
    } else {
      setSearchTarget({ ...target, used: false });
    }
  };

  const addSearchedPoint = () => {
    if (!searchTarget || searchTarget.used) return;
    addPoint({ lat: searchTarget.lat, lng: searchTarget.lng });
    setSearchTarget({ ...searchTarget, used: true });
  };

  const center = useMemo(
    () => (points.length ? [points[0].lat, points[0].lng] : FALLBACK_CENTER),
    // Only the first centre matters; FitToCourse frames every course after it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const km = totalDistanceKm(points);
  const loop = isLoop(points);
  const editable = !readOnly && points.length <= MAX_HANDLES;
  const positions = points.map((p) => [p.lat, p.lng]);
  const last = points.length - 1;

  return (
    <div className="orgr-drawer">
      {!readOnly && (
        <div className="orgr-searchrow">
          <PlaceSearch onPick={onSearchPick} />
          {searchTarget && !searchTarget.used && (
            <button type="button" className="orgr-tool" onClick={addSearchedPoint}>
              <i className="bi bi-plus-circle" aria-hidden="true" /> Add here as next point
            </button>
          )}
          {searchTarget && searchTarget.used && points.length === 1 && (
            <span className="orgf-help orgr-searchnote" role="status">
              Start set. Click the map to add the next point.
            </span>
          )}
        </div>
      )}

      {!readOnly && (
        <div className="orgr-tools" role="toolbar" aria-label="Course drawing tools">
          <button type="button" className="orgr-tool" onClick={undo} disabled={!history.length}>
            <i className="bi bi-arrow-counterclockwise" aria-hidden="true" /> Undo
          </button>
          <button
            type="button" className="orgr-tool"
            onClick={() => removePoint(last)} disabled={!points.length}
          >
            <i className="bi bi-backspace" aria-hidden="true" /> Remove last point
          </button>
          <button
            type="button" className="orgr-tool"
            onClick={closeLoop} disabled={points.length < 3 || loop}
          >
            <i className="bi bi-arrow-repeat" aria-hidden="true" /> Close loop
          </button>
          <button type="button" className="orgr-tool" onClick={reverse} disabled={points.length < 2}>
            <i className="bi bi-arrow-left-right" aria-hidden="true" /> Reverse
          </button>
          <button type="button" className="orgr-tool" onClick={clear} disabled={!points.length}>
            <i className="bi bi-trash3" aria-hidden="true" /> Clear
          </button>
          <span className="orgr-stats" aria-live="polite">
            <b data-testid="route-distance">{km.toFixed(2)}</b> km
            <span className="orgr-dot" aria-hidden="true">·</span>
            <b data-testid="route-points">{points.length}</b> {points.length === 1 ? 'point' : 'points'}
            {loop && <span className="orgr-loop">Loop</span>}
          </span>
        </div>
      )}

      <div className="orgr-map">
        <MapContainer
          center={center}
          zoom={FALLBACK_ZOOM}
          scrollWheelZoom={scrollWheelZoom}
          doubleClickZoom={false}
          style={{ height, width: '100%' }}
          aria-label="Course map"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitToCourse points={points} fitKey={fitKey} />
          <FlyTo target={searchTarget} />
          <AddOnClick onAdd={addPoint} enabled={!readOnly} />

          {positions.length > 1 && (
            <>
              {/* White casing under the line, so it reads on any tile colour. */}
              <Polyline
                positions={positions}
                pathOptions={{ color: '#ffffff', weight: 8, opacity: 0.9 }}
                interactive={false}
              />
              <Polyline
                positions={positions}
                pathOptions={{ color: LINE_COLOUR, weight: 4.5 }}
                bubblingMouseEvents={false}
                eventHandlers={readOnly ? undefined : {
                  click: (event) => insertPoint({ lat: event.latlng.lat, lng: event.latlng.lng }),
                }}
              />
            </>
          )}

          {editable && points.map((p, i) => {
            if (i === 0 || (i === last && last > 0)) return null;
            return (
              <Marker
                // Index keys are right here: a point IS its position in the line.
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                position={[p.lat, p.lng]}
                icon={VERTEX_ICON}
                draggable
                eventHandlers={{
                  dragend: (event) => {
                    const { lat, lng } = event.target.getLatLng();
                    movePoint(i, { lat, lng });
                  },
                  contextmenu: () => removePoint(i),
                }}
              />
            );
          })}

          {points.length > 0 && (
            <Marker
              position={[points[0].lat, points[0].lng]}
              icon={START_ICON}
              draggable={editable}
              title="Start"
              eventHandlers={editable ? {
                dragend: (event) => {
                  const { lat, lng } = event.target.getLatLng();
                  movePoint(0, { lat, lng });
                },
                contextmenu: () => removePoint(0),
              } : undefined}
            />
          )}
          {last > 0 && !loop && (
            <Marker
              position={[points[last].lat, points[last].lng]}
              icon={FINISH_ICON}
              draggable={editable}
              title="Finish"
              eventHandlers={editable ? {
                dragend: (event) => {
                  const { lat, lng } = event.target.getLatLng();
                  movePoint(last, { lat, lng });
                },
                contextmenu: () => removePoint(last),
              } : undefined}
            />
          )}
        </MapContainer>
      </div>

      {!readOnly && (
        <p className="orgf-help">
          {points.length > MAX_HANDLES
            ? `This course has ${points.length} points — too many to edit one by one. You can still extend it, undo, reverse or clear it.`
            : 'Click the map to add the next point. Click the line to add a bend, drag a point to move it, right-click (or long-press) a point to remove it.'}
        </p>
      )}
    </div>
  );
}
